import { useState } from 'react';
import {
  IComposableDashboardCollectionsContext,
  IComposableDashboardCollection,
  assignUnAssignToClients,
  assignUnAssignToClientSpoc,
  assignUnAssignToOrganisationalUnitAccountManagers,
  assignUnAssignToOrganisationalUnitTeams,
  assignUnAssignToTeams,
  assignUnAssignToAccountManagers,
  assignUnAssignToBusinessUnit,
  assignUnAssignToClientUnit,
  assignUnAssignToClientContactUnit,
  assignUnAssignToUsers,
  assignUnAssignToJobs,
} from 'wisestep-api';

import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import { useComposableDashboardCollectionsContext } from './useComposableDashboardCollectionsContext';

export const enum AssignDialogType {
  None = '',
  Clients = 'Clients',
  ClientSpoc = 'Client Spoc',
  AccountManagerOU = 'Account Manager OU',
  TeamsOU = 'Teams OU',
  Teams = 'Teams',
  AccountManagers = 'Account Managers',
  BusinessDefinition = 'Business Definition',
  ClientUnit = 'Client Unit',
  ClientContactUnit = 'Client Contact Unit',
  Users = 'Users',
  Jobs = 'Jobs',
}

export const getDashboardAssignee = (dashboardCollection: IComposableDashboardCollection) => {
  const assigned: AssignDialogType[] = [];
  if (dashboardCollection?.assignToClients) assigned.push(AssignDialogType.Clients);
  if (dashboardCollection?.assignToClientSpoc) assigned.push(AssignDialogType.ClientSpoc);
  if (dashboardCollection?.assignToAccountManagerOU)
    assigned.push(AssignDialogType.AccountManagerOU);
  if (dashboardCollection?.assignToTeamsOU) assigned.push(AssignDialogType.TeamsOU);
  if (dashboardCollection?.assignToTeams) assigned.push(AssignDialogType.Teams);
  if (dashboardCollection?.assignToAccountManagers) assigned.push(AssignDialogType.AccountManagers);
  if (dashboardCollection?.assignToBusinessUnit) assigned.push(AssignDialogType.BusinessDefinition);
  if (dashboardCollection?.assignToClientUnit) assigned.push(AssignDialogType.ClientUnit);
  if (dashboardCollection?.assignToClientContactUnit)
    assigned.push(AssignDialogType.ClientContactUnit);
  if (dashboardCollection?.assignToUsers) assigned.push(AssignDialogType.Users);
  if (dashboardCollection?.assignToJobs) assigned.push(AssignDialogType.Jobs);
  return assigned;
};

const typeFieldMap: Record<AssignDialogType, string> = {
  [AssignDialogType.Clients]: 'assignToClients',
  [AssignDialogType.ClientSpoc]: 'assignToClientSpoc',
  [AssignDialogType.AccountManagerOU]: 'assignToAccountManagerOU',
  [AssignDialogType.TeamsOU]: 'assignToTeamsOU',
  [AssignDialogType.Teams]: 'assignToTeams',
  [AssignDialogType.AccountManagers]: 'assignToAccountManagers',
  [AssignDialogType.BusinessDefinition]: 'assignToBusinessUnit',
  [AssignDialogType.ClientUnit]: 'assignToClientUnit',
  [AssignDialogType.ClientContactUnit]: 'assignToClientContactUnit',
  [AssignDialogType.Users]: 'assignToUsers',
  [AssignDialogType.Jobs]: 'assignToJobs',
  [AssignDialogType.None]: '',
};

const useHandleAssignFlow = () => {
  const { setDashboardCollections, selectedDashboardCollection, setSelectedCollection } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [assignUnassignDialog, setAssignUnassignDialog] = useState<AssignDialogType>(
    AssignDialogType.None
  );

  const assignedTo = getDashboardAssignee(selectedDashboardCollection);

  const typeApiMap: Record<
    AssignDialogType,
    (request: IComposableDashboardCollection) => Promise<{ data: IComposableDashboardCollection }>
  > = {
    [AssignDialogType.Clients]: assignUnAssignToClients,
    [AssignDialogType.ClientSpoc]: assignUnAssignToClientSpoc,
    [AssignDialogType.AccountManagerOU]: assignUnAssignToOrganisationalUnitAccountManagers,
    [AssignDialogType.TeamsOU]: assignUnAssignToOrganisationalUnitTeams,
    [AssignDialogType.Teams]: assignUnAssignToTeams,
    [AssignDialogType.AccountManagers]: assignUnAssignToAccountManagers,
    [AssignDialogType.BusinessDefinition]: assignUnAssignToBusinessUnit,
    [AssignDialogType.ClientUnit]: assignUnAssignToClientUnit,
    [AssignDialogType.ClientContactUnit]: assignUnAssignToClientContactUnit,
    [AssignDialogType.Users]: assignUnAssignToUsers,
    [AssignDialogType.Jobs]: assignUnAssignToJobs,
    [AssignDialogType.None]: () => Promise.resolve({ data: selectedDashboardCollection }),
  };

  const handleAssignUnassignOperation = async (successMessageEntity: string) => {
    setAssignUnassignDialog(AssignDialogType.None);
    const currentValue = assignedTo.includes(assignUnassignDialog);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        [typeFieldMap[assignUnassignDialog]]: !currentValue,
      };

      const response = await typeApiMap[assignUnassignDialog](request);
      setSelectedCollection(response.data);
      setDashboardCollections((prev) => {
        return prev.map((collection) =>
          collection.id === response.data?.id ? response.data : collection
        );
      });

      const action = currentValue ? 'Unassigned from' : 'Assigned to';
      setNotification(
        success(
          `Dashboard Collection ${
            selectedDashboardCollection?.name ?? ''
          } has been ${action} ${successMessageEntity}`
        )
      );
    } catch (error) {
      const action = currentValue ? 'Unassign' : 'Assign';
      setNotification(
        failed(
          `Failed to ${action} the dashboard collection to ${successMessageEntity}. Please try again later!`
        )
      );
    }
  };

  return {
    assignUnassignDialog,
    setAssignUnassignDialog,
    handleAssignUnassignOperation,
    assignedTo,
  };
};

export default useHandleAssignFlow;
